// Uniswap v3 math (exact BigInt ports of TickMath / LiquidityAmounts).
export const Q96 = 2n ** 96n;
const MAX_U256 = 2n ** 256n - 1n;
export const MIN_TICK = -887272;
export const MAX_TICK = 887272;
export const FEE_SPACING = { 100: 1, 500: 10, 3000: 60, 10000: 200 };
export const MAX_U128 = 2n ** 128n - 1n;

const C = [
  [0x2n, 0xfff97272373d413259a46990580e213an], [0x4n, 0xfff2e50f5f656932ef12357cf3c7fdccn], [0x8n, 0xffe5caca7e10e4e61c3624eaa0941cd0n],
  [0x10n, 0xffcb9843d60f6159c9db58835c926644n], [0x20n, 0xff973b41fa98c081472e6896dfb254c0n], [0x40n, 0xff2ea16466c96a3843ec78b326b52861n],
  [0x80n, 0xfe5dee046a99a2a811c461f1969c3053n], [0x100n, 0xfcbe86c7900a88aedcffc83b479aa3a4n], [0x200n, 0xf987a7253ac413176f2b074cf7815e54n],
  [0x400n, 0xf3392b0822b70005940c7a398e4b70f3n], [0x800n, 0xe7159475a2c29b7443b29c7fa6e889d9n], [0x1000n, 0xd097f3bdfd2022b8845ad8f792aa5825n],
  [0x2000n, 0xa9f746462d870fdf8a65dc1f90e061e5n], [0x4000n, 0x70d869a156d2a1b890bb3df62baf32f7n], [0x8000n, 0x31be135f97d08fd981231505542fcfa6n],
  [0x10000n, 0x9aa508b5b7a84e1c677de54f3e99bc9n], [0x20000n, 0x5d6af8dedb81196699c329225ee604n], [0x40000n, 0x2216e584f5fa1ea926041bedfe98n],
  [0x80000n, 0x48a170391f7dc42444e8fa2n],
];

export function sqrtAtTick(tick) {
  const abs = BigInt(Math.abs(tick));
  let r = abs & 1n ? 0xfffcb933bd6fad37aa2d162d1a594001n : 0x100000000000000000000000000000000n;
  for (const [bit, m] of C) if (abs & bit) r = (r * m) >> 128n;
  if (tick > 0) r = MAX_U256 / r;
  return (r >> 32n) + (r % (1n << 32n) === 0n ? 0n : 1n);
}

export const usableTick = (t, spacing) => {
  const lo = Math.ceil(MIN_TICK / spacing) * spacing;
  const hi = Math.floor(MAX_TICK / spacing) * spacing;
  return Math.min(hi, Math.max(lo, Math.round(t / spacing) * spacing));
};
export const fullRange = (spacing) => [Math.ceil(MIN_TICK / spacing) * spacing, Math.floor(MAX_TICK / spacing) * spacing];

// Price helpers. "price" = quote units per 1 base token (human units).
export const rawFromPrice = (price, d0, d1, baseIsToken0) => (baseIsToken0 ? price : 1 / price) * 10 ** (d1 - d0);
export const tickFromPrice = (price, d0, d1, baseIsToken0) => Math.floor(Math.log(rawFromPrice(price, d0, d1, baseIsToken0)) / Math.log(1.0001));
export const priceFromTick = (tick, d0, d1, baseIsToken0) => {
  const raw = 1.0001 ** tick / 10 ** (d1 - d0);
  return baseIsToken0 ? raw : 1 / raw;
};
export const priceFromSqrt = (sqrtP, d0, d1, baseIsToken0) => {
  const r = (Number(sqrtP) / 2 ** 96) ** 2 / 10 ** (d1 - d0);
  return baseIsToken0 ? r : 1 / r;
};
export function sqrtFromPrice(price, d0, d1, baseIsToken0) {
  const raw = rawFromPrice(price, d0, d1, baseIsToken0);
  const s = Math.sqrt(raw);
  if (!isFinite(s) || s <= 0) throw new Error("Invalid price");
  // scale through 2^48 twice to keep float precision before converting to BigInt
  const hi = BigInt(Math.floor(s * 2 ** 48));
  const v = hi * 2n ** 48n;
  if (v <= sqrtAtTick(MIN_TICK) || v >= sqrtAtTick(MAX_TICK)) throw new Error("Price out of the supported range");
  return v;
}

const sortAB = (a, b) => (a > b ? [b, a] : [a, b]);
const L0 = (a, b, amt) => { [a, b] = sortAB(a, b); return (amt * ((a * b) / Q96)) / (b - a); };
const L1 = (a, b, amt) => { [a, b] = sortAB(a, b); return (amt * Q96) / (b - a); };
export const amount0For = (a, b, L) => { [a, b] = sortAB(a, b); return ((L << 96n) * (b - a)) / b / a; };
export const amount1For = (a, b, L) => { [a, b] = sortAB(a, b); return (L * (b - a)) / Q96; };

export function liquidityFor(sp, sa, sb, a0, a1) {
  [sa, sb] = sortAB(sa, sb);
  if (sp <= sa) return L0(sa, sb, a0);
  if (sp < sb) { const x = L0(sp, sb, a0); const y = L1(sa, sp, a1); return x < y ? x : y; }
  return L1(sa, sb, a1);
}
export function amountsFor(sp, sa, sb, L) {
  [sa, sb] = sortAB(sa, sb);
  if (sp <= sa) return [amount0For(sa, sb, L), 0n];
  if (sp < sb) return [amount0For(sp, sb, L), amount1For(sa, sp, L)];
  return [0n, amount1For(sa, sb, L)];
}
// Given one side's amount, the other side required at the current price for the chosen range.
export function pairedAmount(sp, sa, sb, amount, isToken0) {
  [sa, sb] = sortAB(sa, sb);
  if (isToken0) {
    if (sp >= sb) return null;
    if (sp <= sa) return 0n;
    return amount1For(sa, sp, L0(sp, sb, amount)) + 1n;
  }
  if (sp <= sa) return null;
  if (sp >= sb) return 0n;
  return amount0For(sp, sb, L1(sa, sp, amount)) + 1n;
}
export const withSlippage = (x, bps) => (x * BigInt(10000 - bps)) / 10000n;
